import { Link, useLocation } from "react-router";
import { Heart, Bot, Calendar, Gamepad2, User } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTransitionNavigate } from "@/hooks/useTransitionNavigate";

const tabs = [
  { icon: Bot, label: "AI Assistant", href: "/ai-assistant" },
  { icon: Calendar, label: "Events", href: "/events" },
  { icon: Heart, label: "Wishlist", href: "/wishlist", center: true },
  { icon: Gamepad2, label: "Games", href: "/games" },
  { icon: User, label: "Profile", href: "/menu" },
];

export const MobileBottomNav = () => {
  const location = useLocation();
  const navigate = useTransitionNavigate();

  const handleNavClick = (href: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    if (location.pathname === href) return;
    navigate(href);
  };

  const isActive = (href: string) => {
    if (href === "/games") {
      return ["/games", "/randomizer", "/friend-quiz", "/mafia", "/sync"].some(
        (p) => location.pathname === p || location.pathname.startsWith(p + "/")
      );
    }
    if (href === "/menu") {
      return [
        "/menu",
        "/settings",
        "/referrals",
        "/reminders",
        "/friends",
        "/about",
        "/contact",
        "/faq",
        "/privacy-policy",
        "/terms-of-service",
        "/impressum",
      ].some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
    }
    return location.pathname === href || location.pathname.startsWith(href + "/");
  };

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-t border-gray-200 dark:border-gray-800 pb-[env(safe-area-inset-bottom)]"
      aria-label="Bottom navigation"
    >
      <ul className="flex items-end justify-around h-16 px-2">
        {tabs.map((tab) => {
          const active = isActive(tab.href);
          const Icon = tab.icon;

          if (tab.center) {
            return (
              <li key={tab.href} className="flex-1 flex justify-center">
                <Link
                  to={tab.href}
                  onClick={handleNavClick(tab.href)}
                  className="relative -mt-6 flex flex-col items-center"
                  aria-label={tab.label}
                >
                  <div
                    className={cn(
                      "h-14 w-14 rounded-full flex items-center justify-center shadow-lg ring-4 ring-white dark:ring-gray-900 transition-all",
                      active
                        ? "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground scale-105"
                        : "bg-gradient-to-br from-primary/90 to-primary/70 text-primary-foreground"
                    )}
                  >
                    <Icon className="h-7 w-7" />
                  </div>
                  <span
                    className={cn(
                      "mt-1 text-[10px] font-medium",
                      active ? "text-primary" : "text-gray-600 dark:text-gray-300"
                    )}
                  >
                    {tab.label}
                  </span>
                </Link>
              </li>
            );
          }

          return (
            <li key={tab.href} className="flex-1">
              <Link
                to={tab.href}
                onClick={handleNavClick(tab.href)}
                className={cn(
                  "h-full flex flex-col items-center justify-center gap-1 py-2 transition-colors",
                  active
                    ? "text-primary"
                    : "text-gray-500 dark:text-gray-400 hover:text-primary"
                )}
                aria-label={tab.label}
              >
                <Icon className={cn("h-6 w-6", active && "scale-110")} />
                <span className="text-[10px] font-medium leading-none">{tab.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
